import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import type { QcItem, QcItemState } from './contractsMirror.js';
import { QC_ITEM_STATES, QC_OUTSTANDING_STATES } from './contractsMirror.js';
import { useQcAnswerMutation, useQcPhotoMutation, useQcWorkflowMutation } from './useQc.js';

/**
 * One item on a checklist.
 *
 * BUILT FOR A PHONE HELD IN A HOUSE. The four states are a row of real buttons, not a select: a
 * crew member marks these with a thumb, wearing gloves, in bad light. Selecting is one tap and
 * saves immediately — there is no form-level Save, because a checklist runs to ~300 items and
 * nobody should be able to lose a section by navigating away.
 *
 * PASS IS NOT THE DEFAULT AND NOTHING IS PRE-SELECTED. An unanswered item reads as unanswered. The
 * paper's own instructions say the checklist is intentionally broad and not every item applies, so
 * N/A is a real answer rather than a way of dodging one — pre-ticking Pass would quietly turn the
 * whole thing into a formality.
 *
 * The server refuses a Pass on a photo-required item with no photo. That refusal is surfaced on the
 * item rather than as a page-level error, because it is about THIS row and the fix is here.
 *
 * WAIVE lives here too, for whoever may sign off. A failing or needs-review item blocks sign-off
 * until it is fixed or waived, and a waiver needs a reason because it goes on the record with the
 * waiver's name. The server re-checks every waive; its refusal shows on this row.
 */

/**
 * The four states, on the kit's existing semantic tokens — success / destructive / warning — rather
 * than new QC-specific colours. Both themes are already tuned for these, and a fifth colour system
 * for one feature is how a design system stops being one.
 *
 * Colour is never the only signal: each button carries its own label, and `aria-pressed` reports
 * the selection to a screen reader (A11Y-01).
 */
const STATE_STYLES: Record<QcItemState, string> = {
  pass: 'border-success/40 bg-success/12 text-success',
  fail: 'border-destructive/40 bg-destructive/10 text-destructive',
  needs_review: 'border-warning/40 bg-warning/12 text-warning',
  na: 'border-border bg-secondary text-muted-foreground',
};

const CONTROL =
  'mt-1 w-full min-w-0 rounded-md border border-border bg-background px-3 py-2 text-foreground text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background';

export interface QcItemRowProps {
  checklistId: number;
  item: QcItem;
  canAnswer: boolean;
  /** May waive a failing item. The caller derives it from the server's `canApprove`. */
  canWaive?: boolean;
}

export function QcItemRow({ checklistId, item, canAnswer, canWaive = false }: QcItemRowProps) {
  const { t } = useTranslation();
  const answer = useQcAnswerMutation(checklistId);
  const photo = useQcPhotoMutation(checklistId);
  const waive = useQcWorkflowMutation(checklistId);
  const [note, setNote] = useState(item.note ?? '');
  const [value, setValue] = useState(item.valueText ?? '');
  const [approvedBy, setApprovedBy] = useState(item.approvedByText ?? '');
  const [waiving, setWaiving] = useState(false);
  const [waiveReason, setWaiveReason] = useState('');

  // The waive refusal wins: an older answer or photo error must not stand in for it.
  const failure = waive.error ?? answer.error ?? photo.error;
  const errorMessage =
    failure instanceof ApiError
      ? t(`qc:errors.${failure.message}`, { defaultValue: t('qc:errors.generic') })
      : waive.error
        ? t('qc:errors.generic')
        : null;

  const showWaive =
    canWaive &&
    item.itemType === 'check' &&
    item.state !== null &&
    QC_OUTSTANDING_STATES.includes(item.state) &&
    !item.waiver;

  const submitWaiver = () => {
    const reason = waiveReason.trim();
    if (!reason) return;
    waive.mutate(
      { action: 'waive', itemId: item.id, reason },
      {
        onSuccess: () => {
          setWaiving(false);
          setWaiveReason('');
        },
      },
    );
  };

  const setState = (next: QcItemState) => {
    if (!canAnswer) return;
    // Tapping the current state clears it, so a mis-tap is one tap to undo rather than a state you
    // cannot get out of.
    answer.mutate({
      itemId: item.id,
      state: item.state === next ? null : next,
      note: note || null,
    });
  };

  const saveText = () => {
    if (!canAnswer) return;
    answer.mutate({
      itemId: item.id,
      valueText: value || null,
      approvedByText: item.itemType === 'selection' ? approvedBy || null : null,
      note: note || null,
    });
  };

  return (
    <li className="border-border border-b px-4 py-3 last:border-b-0">
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="min-w-0 flex-1 text-foreground text-sm">
            {item.label}
            {item.photoRequired ? (
              <span className="ml-2 whitespace-nowrap rounded bg-secondary px-1.5 py-0.5 font-medium text-muted-foreground text-xs">
                {t('qc:item.photoRequired')}
              </span>
            ) : null}
          </p>
          {item.answeredByName ? (
            <span className="whitespace-nowrap text-muted-foreground text-xs">
              {t('qc:item.answeredBy', { name: item.answeredByName })}
            </span>
          ) : null}
        </div>

        {item.itemType === 'check' ? (
          // A fieldset, not a div with role="group": same group role and name for assistive tech,
          // from the native element. The resets undo the UA's border, padding and min-content width.
          <fieldset
            className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0"
            aria-label={item.label}
          >
            {QC_ITEM_STATES.map((state) => {
              const active = item.state === state;
              return (
                <button
                  key={state}
                  type="button"
                  // 44px min target (A11Y-01) — this is tapped with a thumb, not clicked.
                  className={`min-h-11 min-w-20 rounded-md border px-3 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                    active
                      ? STATE_STYLES[state]
                      : 'border-border bg-background text-muted-foreground'
                  } ${canAnswer ? '' : 'cursor-not-allowed opacity-60'}`}
                  aria-pressed={active}
                  disabled={!canAnswer || answer.isPending}
                  onClick={() => setState(state)}
                >
                  {t(`qc:states.${state}`)}
                </button>
              );
            })}
          </fieldset>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="min-w-0 flex-1">
              <label
                htmlFor={`qc-value-${item.id}`}
                className="font-medium text-foreground text-xs"
              >
                {t(item.itemType === 'selection' ? 'qc:item.approvedDetail' : 'qc:item.value')}
              </label>
              <input
                id={`qc-value-${item.id}`}
                type="text"
                className={CONTROL}
                value={value}
                maxLength={500}
                disabled={!canAnswer}
                onChange={(e) => setValue(e.target.value)}
                onBlur={saveText}
              />
            </div>
            {item.itemType === 'selection' ? (
              <div className="min-w-0 sm:w-52">
                <label
                  htmlFor={`qc-approver-${item.id}`}
                  className="font-medium text-foreground text-xs"
                >
                  {t('qc:item.approvedBy')}
                </label>
                <input
                  id={`qc-approver-${item.id}`}
                  type="text"
                  className={CONTROL}
                  value={approvedBy}
                  maxLength={160}
                  disabled={!canAnswer}
                  onChange={(e) => setApprovedBy(e.target.value)}
                  onBlur={saveText}
                />
              </div>
            ) : null}
          </div>
        )}

        {item.itemType === 'check' ? (
          <div className="flex flex-wrap items-center gap-3">
            {canAnswer ? (
              <label
                className="inline-flex min-h-11 cursor-pointer items-center rounded-md border border-border bg-background px-3 font-medium text-foreground text-sm focus-within:ring-2 focus-within:ring-ring"
                htmlFor={`qc-photo-${item.id}`}
              >
                {photo.isPending ? t('qc:item.uploading') : t('qc:item.addPhoto')}
                <input
                  id={`qc-photo-${item.id}`}
                  type="file"
                  // `capture` opens the camera directly on a phone rather than a file browser,
                  // which is where these are taken.
                  accept="image/*"
                  capture="environment"
                  className="sr-only"
                  disabled={photo.isPending}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) photo.mutate({ itemId: item.id, file });
                    // Clear the input so re-picking the same file still fires a change.
                    e.target.value = '';
                  }}
                />
              </label>
            ) : null}
            {item.photoCount > 0 ? (
              <span className="text-muted-foreground text-xs">
                {t('qc:item.photoCount', { count: item.photoCount })}
              </span>
            ) : null}
          </div>
        ) : null}

        {item.itemType === 'check' && canAnswer ? (
          <div>
            <label htmlFor={`qc-note-${item.id}`} className="font-medium text-foreground text-xs">
              {t('qc:item.note')}
            </label>
            <input
              id={`qc-note-${item.id}`}
              type="text"
              className={CONTROL}
              value={note}
              maxLength={1000}
              placeholder={t('qc:item.notePlaceholder')}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => {
                if (note !== (item.note ?? '')) {
                  answer.mutate({ itemId: item.id, state: item.state ?? null, note: note || null });
                }
              }}
            />
          </div>
        ) : null}

        {item.waiver ? (
          <p className="text-muted-foreground text-xs">
            {t('qc:item.waived', { reason: item.waiver.reason })}
          </p>
        ) : null}

        {showWaive && !waiving ? (
          <div>
            <Button
              type="button"
              variant="secondary"
              className="min-h-11"
              onClick={() => {
                answer.reset();
                photo.reset();
                waive.reset();
                setWaiving(true);
              }}
            >
              {t('qc:item.waive')}
            </Button>
          </div>
        ) : null}

        {showWaive && waiving ? (
          <form
            className="flex flex-col gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              submitWaiver();
            }}
          >
            <div>
              <label
                htmlFor={`qc-waive-reason-${item.id}`}
                className="font-medium text-foreground text-xs"
              >
                {t('qc:item.waiveReason')}
              </label>
              <textarea
                id={`qc-waive-reason-${item.id}`}
                className={CONTROL}
                value={waiveReason}
                required
                rows={2}
                maxLength={1000}
                aria-describedby={`qc-waive-hint-${item.id}`}
                onChange={(e) => setWaiveReason(e.target.value)}
              />
              <p id={`qc-waive-hint-${item.id}`} className="mt-1 text-muted-foreground text-xs">
                {t('qc:item.waiveHint')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="submit"
                className="min-h-11"
                disabled={!waiveReason.trim() || waive.isPending}
              >
                {waive.isPending ? t('qc:item.waiving') : t('qc:item.waiveSubmit')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="min-h-11"
                disabled={waive.isPending}
                onClick={() => {
                  waive.reset();
                  setWaiving(false);
                  setWaiveReason('');
                }}
              >
                {t('qc:item.waiveCancel')}
              </Button>
            </div>
          </form>
        ) : null}

        {errorMessage ? (
          <p role="alert" className="text-destructive text-xs">
            {errorMessage}
          </p>
        ) : null}
      </div>
    </li>
  );
}
